import React from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Grid,
  Alert,
  Button,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import * as XLSX from "xlsx";
import { useUserManagement } from "./useUserManagement";
import { fetchUsersApi } from "../../../utils/api/admin";
import UsersList from "./UsersList/UsersList";
import DeleteUserDialog from "./DeleteUserDialog/DeleteUserDialog";
import EditUserDialog from "./EditUserDialog/EditUserDialog";
import { Attendee } from "../../../redux/types/types";

export const UserManagement: React.FC = () => {
  const intl = useIntl();
  const {
    users,
    totalUsers,
    error,
    deleteDialogOpen,
    editDialogOpen,
    userToEdit,
    page,
    rowsPerPage,
    orderBy,
    order,
    searchTerm,
    updateError,
    setUpdateError,
    fetchUserData,
    handleDeleteUser,
    confirmDeleteUser,
    handleEditUser,
    handleResetPassword,
    handleChangePage,
    handleChangeRowsPerPage,
    handleRequestSort,
    handleSearchChange,
    setDeleteDialogOpen,
    setEditDialogOpen,
    onUserUpdate,
  } = useUserManagement();

  React.useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const formatDate = React.useCallback(
    (value?: Date | string | null) => {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return intl.formatDate(date, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },
    [intl],
  );

  const handleExportToExcel = React.useCallback(async () => {
    try {
      const getFormattedDateTime = () => {
        const now = new Date();
        return now.toISOString().replace(/[:.]/g, "-").slice(0, -5);
      };

      const pageSize = 500;
      const firstPage = await fetchUsersApi({
        page: 1,
        limit: pageSize,
        sortBy: "id",
        sortOrder: "asc",
        search: "",
      });
      const total = firstPage.data?.total ?? 0;
      const pages = Math.max(1, Math.ceil(total / pageSize));
      const pageRequests = [];

      for (let page = 2; page <= pages; page += 1) {
        pageRequests.push(
          fetchUsersApi({
            page,
            limit: pageSize,
            sortBy: "id",
            sortOrder: "asc",
            search: "",
          }),
        );
      }

      const restPages = await Promise.all(pageRequests);
      const allUsers = [
        ...(firstPage.data?.users ?? []),
        ...restPages.flatMap((response) => response.data?.users ?? []),
      ];

      if (allUsers.length === 0) return;

      const rows = allUsers.flatMap((user) => {
        const attendees =
          user.attendees && user.attendees.length > 0 ? user.attendees : [null];

        return attendees.map((attendee: Attendee | null) => ({
          "User ID": user.id ?? "",
          "User First Name": user.firstname ?? "",
          "User Last Name": user.lastname ?? "",
          "User Email": user.email ?? "",
          "User Phone": user.phone ?? "",
          "User Created At": formatDate(user.createdAt ?? null),
          "Data Usage Agreed": user.dataUsageAgreed ? "Yes" : "No",
          "Photo Agreed": user.photoAgreed ? "Yes" : "No",
          "Attendee ID": attendee?.id ?? "",
          "Attendee First Name": attendee?.firstName ?? "",
          "Attendee Last Name": attendee?.lastName ?? "",
          "Attendee Email": attendee?.email ?? "",
          "Attendee Phone": attendee?.phone ?? "",
          "Attendee Birthdate": formatDate(attendee?.birthdate ?? null),
          "Attendee Sex": attendee?.sex ?? "",
          "Attendee Postal Code": attendee?.postalCode ?? "",
          "Attendee Permis Status": attendee?.permisStatus ?? "",
          "Attendee Created At": formatDate(attendee?.createdAt ?? null),
        }));
      });

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
      XLSX.writeFile(workbook, `users_${getFormattedDateTime()}.xlsx`);
    } catch (error) {
      console.error("Failed to export users to Excel:", error);
    }
  }, [formatDate]);

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper sx={{ width: "100%", mb: 2 }}>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <Typography variant="h6" component="div">
                <FormattedMessage id="admin.dashboard.users" />
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Grid
                  item
                  mr={2}
                  xs={12}
                  sm={6}
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "flex-start", sm: "flex-end" },
                    alignItems: "end",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleExportToExcel}
                    disabled={totalUsers === 0}
                  >
                    <FormattedMessage
                      id="admin.dashboard.reports.exportExcel"
                      defaultMessage="Export to Excel"
                    />
                  </Button>
                </Grid>
                <SearchIcon sx={{ mr: 1 }} />
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder={intl.formatMessage({
                    id: "admin.dashboard.searchPlaceholder",
                  })}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  fullWidth
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
        <UsersList
          users={users}
          totalUsers={totalUsers}
          page={page}
          rowsPerPage={rowsPerPage}
          orderBy={orderBy}
          order={order}
          onRequestSort={handleRequestSort}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          onEditUser={handleEditUser}
          onDeleteUser={handleDeleteUser}
          onResetPassword={handleResetPassword}
        />
      </Paper>
      <DeleteUserDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDeleteUser}
      />
      <EditUserDialog
        open={editDialogOpen}
        userToEdit={userToEdit}
        onClose={() => {
          setEditDialogOpen(false);
          setUpdateError(null);
        }}
        onSave={onUserUpdate}
        error={updateError}
      />
    </Box>
  );
};
